import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Dumbbell, Target, Timer } from "lucide-react";

const SportsWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const sportsTab = tabs.find((tab) => tab.id === "sports");
    const widget = sportsTab?.widget || {};

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            {/* Main widget */}
            <div className="bg-gradient-to-br from-white/80 via-emerald-50/30 to-green-100/20 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-emerald-200/40 mb-6 hover:shadow-emerald-200/30 hover:shadow-2xl transition-all duration-500 group">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center">
                        <motion.div
                            className={`me-3 p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl shadow-lg`}
                            animate={{ scale: [1, 1.1, 1], rotate: [0, 5, 0, -5, 0] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        >
                            <Dumbbell className="w-6 h-6 text-white" />
                        </motion.div>
                        <div className={isRTL ? "mr-4" : "ml-4"}>
                            <h3 className="text-xl font-bold text-gray-800 mb-1">{widget.title}</h3>
                            <p className="text-sm text-gray-600">{widget.subtitle}</p>
                        </div>
                    </div>
                    <motion.div
                        className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-full text-sm font-medium shadow-lg"
                        animate={{ boxShadow: ["0 4px 15px rgba(16, 185, 129, 0.3)", "0 8px 25px rgba(16, 185, 129, 0.5)", "0 4px 15px rgba(16, 185, 129, 0.3)"] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        {widget.active}
                    </motion.div>
                </div>

                {/* Main stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <motion.div
                        className="text-center p-4 bg-white/50 rounded-2xl backdrop-blur-sm border border-emerald-100/50"
                        whileHover={{ scale: 1.05, y: -2 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <motion.div className="text-xl font-bold text-emerald-600 mb-1" animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
                            {widget.heartRate?.value}
                        </motion.div>
                        <div className="text-xs text-gray-600">{widget.heartRate?.label}</div>
                        <div className="text-xs text-emerald-600 font-medium">{widget.heartRate?.unit}</div>
                    </motion.div>

                    <motion.div
                        className="text-center p-4 bg-white/50 rounded-2xl backdrop-blur-sm border border-emerald-100/50"
                        whileHover={{ scale: 1.05, y: -2 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <motion.div className="text-xl font-bold text-green-600 mb-1" animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}>
                            {widget.calories?.value}
                        </motion.div>
                        <div className="text-xs text-gray-600">{widget.calories?.label}</div>
                    </motion.div>

                    <motion.div
                        className="text-center p-4 bg-white/50 rounded-2xl backdrop-blur-sm border border-emerald-100/50"
                        whileHover={{ scale: 1.05, y: -2 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <motion.div className="text-xl font-bold text-emerald-700 mb-1" animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}>
                            {widget.intensity?.value}
                        </motion.div>
                        <div className="text-xs text-gray-600">{widget.intensity?.label}</div>
                        <div className="text-xs text-emerald-700 font-medium">{widget.intensity?.zone}</div>
                    </motion.div>
                </div>

                {/* Training time progress */}
                <div className="">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">{widget.duration?.label}</span>
                        <span className="text-sm text-emerald-600 font-bold">{widget.duration?.value}</span>
                    </div>
                    <div className="relative h-3 bg-emerald-100 rounded-full overflow-hidden">
                        <motion.div
                            className="absolute top-0 left-0 h-full bg-gradient-to-r from-emerald-500 to-green-600 rounded-full"
                            initial={{ width: "0%" }}
                            animate={{ width: "73%" }}
                            transition={{ duration: 2, ease: "easeOut" }}
                        />
                        <motion.div
                            className="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-transparent via-white/30 to-transparent"
                            animate={{ x: ["-100%", "100%"] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        />
                    </div>
                </div>
            </div>

            {/* Sub-widgets */}
            <div className="grid grid-cols-2 gap-4">
                {/* Training status */}
                <motion.div
                    className="bg-gradient-to-br from-white/80 via-emerald-50/30 to-green-100/20 backdrop-blur-xl rounded-2xl p-4 shadow-lg border border-emerald-200/40 hover:shadow-emerald-200/30 hover:shadow-xl transition-all duration-300 group"
                    whileHover={{ scale: 1.02, y: -2 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <div className="flex items-center justify-between mb-3">
                        <motion.div
                            className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-md"
                            animate={{ rotate: [0, 10, 0, -10, 0] }}
                            transition={{ duration: 3, repeat: Infinity }}
                        >
                            <Target className="w-4 h-4 text-white" />
                        </motion.div>
                        <div className="text-right">
                            <div className="text-lg font-bold text-emerald-600">{widget.trainingStatus?.value}</div>
                            <div className="text-xs text-gray-600">{widget.trainingStatus?.label}</div>
                        </div>
                    </div>
                    <div className="relative h-2 bg-emerald-100 rounded-full overflow-hidden">
                        <motion.div
                            className="absolute top-0 left-0 h-full bg-gradient-to-r from-emerald-500 to-green-600 rounded-full"
                            initial={{ width: "0%" }}
                            animate={{ width: "78%" }}
                            transition={{ duration: 1.5, ease: "easeOut" }}
                        />
                    </div>
                    <div className="text-xs text-emerald-600 font-medium mt-2">{widget.trainingStatus?.intensity}</div>
                </motion.div>

                {/* Recovery */}
                <motion.div
                    className="bg-gradient-to-br from-white/80 via-emerald-50/30 to-green-100/20 backdrop-blur-xl rounded-2xl p-4 shadow-lg border border-emerald-200/40 hover:shadow-emerald-200/30 hover:shadow-xl transition-all duration-300 group"
                    whileHover={{ scale: 1.02, y: -2 }}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <div className="flex items-center justify-between mb-3">
                        <motion.div
                            className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-md"
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            <Timer className="w-4 h-4 text-white" />
                        </motion.div>
                        <div className="text-right">
                            <div className="text-lg font-bold text-green-600">{widget.recovery?.value}</div>
                            <div className="text-xs text-gray-600">{widget.recovery?.label}</div>
                        </div>
                    </div>
                    <div className="flex items-center justify-center h-8">
                        {[...Array(8)].map((_, index) => (
                            <motion.div
                                key={index}
                                className="w-1 h-4 bg-gradient-to-t from-green-400 to-emerald-500 mx-0.5 rounded-full"
                                animate={{ height: ["16px", "8px", "16px"], opacity: [0.7, 1, 0.7] }}
                                transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.1 }}
                            />
                        ))}
                    </div>
                    <div className="text-xs text-green-600 font-medium mt-2">{widget.recovery?.status}</div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default SportsWidget;
